// src/pages/Amostras/AnaliseDetalhesModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Typography,
    Chip,
    Divider,
    Paper,
    CircularProgress,
    LinearProgress,
    Snackbar,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    Grid,
    IconButton,
    useTheme,
    useMediaQuery,
    Box,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as Lucide from 'lucide-react';

import { useParams } from 'react-router-dom';

import {
    iniciarProcessamentoAmostra,
    reprocessarResultadoAmostra,
    buscarUltimoResultadoAmostra,
    excluirResultadosDaAmostra,
} from '../../infra/api/resultado';

import { baixarPdfTemplatePorAmostra } from '../../infra/api/templateGenerateApi';
import { getTowers } from '../../infra/api/Torres';
import { adicionarComentarioAmostra } from '../../infra/api/Amostras';
import { usePermissao } from '../../security/usePermissao';
import { useBootstrap } from '../../context/BootstrapContext';
import EficienciaResumo from '../../components/EficienciaResumo';
import ComentariosAnaliseDialog from '../../components/ComentariosAnaliseDialog';
import {
    estadoResultado,
    LIMITE_PADRAO,
    BUFFER_PADRAO,
} from '../../utils/eficiencia';

const upper = (v) =>
    String(v || '')
        .toUpperCase()
        .trim();

const fmt = (v) =>
    v === null || v === undefined || v === '' ? '—' : String(v);

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

// Resultado "pronto" = o modelo terminou: status terminal do ResultadoAmostra, OU
// todos os itens tem objeto de resultado (ou falharam). Usado para o polling parar.
// (O `invalida` NAO conta: ele e calculado na leitura e fica true tambem enquanto ainda
// nao ha dados — logo nao indica "terminou".)
const isResultadoPronto = (r) => {
    if (!r) return false;
    const st = upper(r.status);
    if (st === 'CONCLUIDO' || st === 'FALHO') return true;
    const exps = Array.isArray(r?.resultadosExperimento)
        ? r.resultadosExperimento
        : [];
    if (exps.length === 0) return false;
    return exps.every(
        (e) => !!e?.resultado || upper(e?.status) === 'FALHO' || !!e?.erro
    );
};

// So faz polling se o resultado foi criado ha pouco (ainda pode estar sendo processado).
// Evita ficar "Calculando..." eternamente / reaparecer no F5 num resultado que ja e final
// (ex.: invalida por dados insuficientes, com itens presos em PENDENTE).
const POLL_FRESH_MS = 90 * 1000;
const resultadoRecente = (r) => {
    const t = r?.createdAt ? Date.parse(r.createdAt) : NaN;
    return Number.isFinite(t) && Date.now() - t < POLL_FRESH_MS;
};

const normalizeId = (val) => {
    if (!val) return '';
    if (typeof val === 'string') {
        const m = val.match(/ObjectId\("([^"]+)"\)/);
        if (m) return m[1];
        try {
            const parsed = JSON.parse(val);
            if (parsed?.$oid) return String(parsed.$oid);
        } catch (e) {}
        return val.trim();
    }
    if (typeof val === 'object' && val?.$oid) return String(val.$oid);
    return String(val).trim();
};

const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString('pt-BR');
};

const msToHuman = (ms) => {
    if (ms === null || ms === undefined || Number.isNaN(ms)) return '—';
    const s = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(s / 60);
    const r = s % 60;
    if (m <= 0) return `${r}s`;
    return `${m}m ${r}s`;
};

const fmtTemp = (v) => {
    if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
    return `${Number(v).toFixed(1).replace('.', ',')}°C`;
};

const fmtPct = (v) => {
    if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
    return `${Number(v).toFixed(0)}%`;
};

const fmtHpa = (v) => {
    if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
    return `${Number(v).toFixed(0)} hPa`;
};

const fmtWind = (v) => {
    if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
    return `${Number(v).toFixed(1).replace('.', ',')} m/s`;
};

const fmtUtcToLocal = (isoUtc) => {
    if (!isoUtc) return '—';
    const d = new Date(isoUtc);
    if (Number.isNaN(d.getTime())) return String(isoUtc);
    return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const buildGraficoPath = (tenant, experimentId) => {
    const id = experimentId ? String(experimentId).trim() : '';
    if (!id) return null;
    const t = tenant ? String(tenant).trim() : '';
    const safeId = encodeURIComponent(id);
    return t ? `/${t}/graficos/${safeId}` : `/graficos/${safeId}`;
};

const getWeatherPresentation = (condition) => {
    const c = upper(condition);
    if (c === 'CLEAR')
        return { label: 'Céu limpo', Icon: Lucide.Sun, chipColor: 'warning' };
    if (c === 'CLOUDS')
        return { label: 'Nublado', Icon: Lucide.Cloud, chipColor: 'info' };
    if (c === 'RAIN' || c === 'DRIZZLE')
        return { label: 'Chuva', Icon: Lucide.CloudRain, chipColor: 'primary' };
    if (c === 'THUNDERSTORM')
        return {
            label: 'Trovoadas',
            Icon: Lucide.CloudLightning,
            chipColor: 'error',
        };
    if (c === 'SNOW')
        return { label: 'Neve', Icon: Lucide.Snowflake, chipColor: 'info' };
    if (['MIST', 'FOG', 'HAZE', 'SMOKE', 'DUST'].includes(c))
        return { label: 'Neblina', Icon: Lucide.Globe, chipColor: 'default' };
    return {
        label: condition || 'Clima',
        Icon: Lucide.Globe,
        chipColor: 'default',
    };
};

const parseLabValue = (value) => {
    if (value === null || value === undefined || value === '—') return NaN;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        if (value.toLowerCase().includes('e')) {
            const parsed = parseFloat(value);
            return isNaN(parsed) ? NaN : parsed;
        }
        const cleanStr = value.replace(/\./g, '').replace(',', '.');
        const parsed = parseFloat(cleanStr);
        return isNaN(parsed) ? NaN : parsed;
    }
    return NaN;
};

const toScientificResult = (value) => {
    const num = parseLabValue(value);
    if (isNaN(num)) return '—';
    if (num === 0) return '0';
    const THRESHOLD_DIGITS = 1000000;
    if (Math.abs(num) >= 0.01 && Math.abs(num) < THRESHOLD_DIGITS) {
        return new Intl.NumberFormat('pt-BR', {
            maximumFractionDigits: 2,
        }).format(num);
    }
    return new Intl.NumberFormat('pt-BR', {
        notation: 'scientific',
        maximumFractionDigits: 2,
    })
        .format(num)
        .toUpperCase();
};

const statusChip = (status) => {
    const s = upper(status);
    if (s === 'PENDENTE') return { label: 'Pendente', color: 'warning' };
    // Amostras rodando no dispositivo — nao e o modelo processando.
    if (s === 'EM_PROCESSAMENTO') return { label: 'Em análise', color: 'info' };
    if (s === 'ENCERRADA') return { label: 'Encerrada', color: 'success' };
    if (s === 'ERRO') return { label: 'Erro', color: 'error' };
    if (s === 'FALHO') return { label: 'Falhou', color: 'error' };
    return { label: s || '—', color: 'default' };
};

const resultadoChip = (status) => {
    const s = upper(status);
    if (s === 'PROCESSADO') return { label: 'Processado', color: 'success' };
    if (s === 'SEM_CRESCIMENTO')
        return { label: 'Sem crescimento', color: 'info' };
    if (s === 'ERRO') return { label: 'Erro', color: 'error' };
    if (s === 'FALHO') return { label: 'Falhou', color: 'error' };
    if (s === 'CONCLUIDO') return { label: 'Concluído', color: 'success' };
    return { label: s || '—', color: 'default' };
};

const ETAPAS = [
    { key: 'CRIADA', label: 'Criada' },
    { key: 'ENVIADA', label: 'Enviada' },
    { key: 'PROCESSAMENTO', label: 'Processando' },
    { key: 'PREDICAO', label: 'Predição' },
    { key: 'FINALIZADA', label: 'Finalizada' },
    { key: 'ERRO', label: 'Erro' },
];

const etapaStatus = (atual, etapa) => {
    if (!atual) return 'pending';
    if (atual === 'ERRO') return etapa === 'ERRO' ? 'active' : 'done';
    const order = ETAPAS.map((e) => e.key);
    const a = order.indexOf(atual);
    const b = order.indexOf(etapa);
    if (b < a) return 'done';
    if (b === a) return 'active';
    return 'pending';
};

// `emCalculo`: enquanto o resultado esta sendo calculado o backend ja gravou
// invalida=true (ele avalia um documento que ainda nao tem leitura de E.coli).
// Sem esse guarda a timeline acendia "Erro" no meio de um processamento normal.
const deriveGlobalStage = (resultadoAmostra, emCalculo = false) => {
    if (!resultadoAmostra) return null;
    if (!emCalculo && resultadoAmostra?.invalida) return 'ERRO';
    const overall = upper(resultadoAmostra?.status);
    if (overall === 'FALHO' || overall === 'ERRO') return 'ERRO';
    const exps = Array.isArray(resultadoAmostra?.resultadosExperimento)
        ? resultadoAmostra.resultadosExperimento
        : [];
    if (!exps.length) return 'CRIADA';
    const anyStarted = exps.some((e) => !!e?.startedAt);
    const anyFinished = exps.some((e) => !!e?.finishedAt);
    const anyHasResult = exps.some((e) => !!e?.resultado);
    const anyError = exps.some(
        (e) => upper(e?.status) === 'FALHO' || !!e?.erro
    );
    if (anyError) return 'ERRO';
    if (anyFinished && anyHasResult) return 'FINALIZADA';
    if (anyHasResult) return 'PREDICAO';
    if (anyStarted) return 'PROCESSAMENTO';
    return 'ENVIADA';
};

const deriveStageFromExperiment = (exp) => {
    const s = upper(exp?.status);
    if (s === 'FALHO' || exp?.erro) return 'ERRO';
    if (exp?.finishedAt) return 'FINALIZADA';
    if (exp?.startedAt) return 'PROCESSAMENTO';
    if (exp?.createdAt) return 'ENVIADA';
    return 'CRIADA';
};

const TimelineEtapas = ({ stage }) => (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
        <Stack spacing={1}>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
            >
                <Typography
                    variant="subtitle2"
                    fontWeight={800}
                    color="text.secondary"
                >
                    TIMELINE DE PROCESSAMENTO
                </Typography>
                {stage && (
                    <Chip
                        size="small"
                        label={stage}
                        color="primary"
                        variant="filled"
                    />
                )}
            </Stack>
            <Divider />
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    alignItems: 'center',
                    pt: 1,
                }}
            >
                {ETAPAS.map((e) => {
                    const st = etapaStatus(stage, e.key);
                    let icon = <Lucide.Circle size={18} />;
                    let color = 'default';
                    let variant = 'outlined';
                    if (st === 'done') {
                        icon = <Lucide.CircleCheck size={18} />;
                        color = 'success';
                        variant = 'filled';
                    } else if (st === 'active') {
                        icon = <Lucide.Clock size={18} />;
                        color = 'primary';
                        variant = 'filled';
                    } else if (e.key === 'ERRO' && st === 'active') {
                        icon = <Lucide.CircleAlert size={18} />;
                        color = 'error';
                    }
                    return (
                        <Chip
                            key={e.key}
                            icon={icon}
                            label={e.label}
                            color={color}
                            variant={variant}
                            size="small"
                            sx={{ fontWeight: st === 'active' ? 700 : 400 }}
                        />
                    );
                })}
            </Box>
        </Stack>
    </Paper>
);

const FieldLine = ({ label, value, mono = false, align = 'left' }) => (
    <Box>
        <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            align={align}
        >
            {label}
        </Typography>
        <Typography
            variant="body2"
            align={align}
            sx={{
                fontFamily: mono ? 'monospace' : 'inherit',
                wordBreak: 'break-all',
                fontWeight: 500,
            }}
        >
            {fmt(value)}
        </Typography>
    </Box>
);

const PredField = ({ label, value, error }) => {
    const hasError = !!error && String(error).trim().length > 0;
    const displayValue = toScientificResult(value);
    const rawValue =
        typeof value === 'number' ||
        (typeof value === 'string' && value.length > 0)
            ? value
            : '';
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1,
                bgcolor: hasError ? 'error.50' : 'background.paper',
                borderColor: hasError ? 'error.main' : undefined,
            }}
        >
            <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                align="center"
            >
                {label}
            </Typography>
            <Tooltip title={String(rawValue)} arrow placement="top">
                <Typography
                    variant="h6"
                    align="center"
                    fontWeight={700}
                    color={hasError ? 'error' : 'text.primary'}
                >
                    {displayValue}
                </Typography>
            </Tooltip>
            {hasError && (
                <Typography
                    variant="caption"
                    color="error"
                    align="center"
                    display="block"
                    sx={{ wordBreak: 'break-word', lineHeight: 1 }}
                >
                    {String(error)}
                </Typography>
            )}
        </Paper>
    );
};

const LocalizacaoClimaCard = ({ amostra }) => {
    const theme = useTheme();
    const { bootstrap } = useBootstrap();
    const client = bootstrap?.client;

    const weather = amostra?.weather || null;
    const lat = amostra?.latitude;
    const lon = amostra?.longitude;
    const tempAmostra = amostra?.temperaturaAmostra;

    const { Icon, label, chipColor } = getWeatherPresentation(
        weather?.condition
    );
    const mapUrl =
        typeof lat === 'number' && typeof lon === 'number'
            ? `https://www.google.com/maps?q=${lat},${lon}`
            : null;
    const hasWeather = !!weather && typeof weather === 'object';

    return (
        <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}
        >
            <Stack spacing={1.5}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Lucide.MapPin color={theme.palette.error.main} />
                        <Typography variant="subtitle2" fontWeight={800}>
                            Local & Clima
                        </Typography>
                    </Stack>
                    {mapUrl && (
                        <Button
                            size="small"
                            variant="text"
                            href={mapUrl}
                            target="_blank"
                            rel="noreferrer"
                            startIcon={<Lucide.Globe color={theme.palette.primary.main} />}
                        >
                            Abrir no mapa
                        </Button>
                    )}
                </Stack>

                <Divider />

                <Box>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 0.5 }}
                    >
                        <Lucide.Building2 size={18} color={theme.palette.action.active} />
                        <Typography variant="caption" color="text.secondary">
                            Local de Referência (Cadastro)
                        </Typography>
                    </Stack>

                    <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                        {client?.city || 'Cidade não identificada'}
                        {client?.state ? ` - ${client?.state}` : ''}
                    </Typography>

                    {(client?.neighborhood || client?.zipCode) && (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                        >
                            {client?.neighborhood && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                >
                                    {client.neighborhood}
                                </Typography>
                            )}
                            {client?.neighborhood && client?.zipCode && (
                                <Typography
                                    variant="caption"
                                    color="text.disabled"
                                >
                                    •
                                </Typography>
                            )}
                            {client?.zipCode && (
                                <Chip
                                    label={`CEP: ${client.zipCode}`}
                                    size="small"
                                    variant="outlined"
                                    icon={<Lucide.Map size={14} />}
                                    sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        bgcolor: 'action.hover',
                                        borderRadius: 1,
                                    }}
                                />
                            )}
                        </Stack>
                    )}

                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{
                            fontFamily: 'monospace',
                            mt: 0.5,
                            display: 'block',
                        }}
                    >
                        Coordenadas da Coleta:{' '}
                        {typeof lat === 'number' && typeof lon === 'number'
                            ? `(${lat.toFixed(4)}, ${lon.toFixed(4)})`
                            : '—'}
                    </Typography>
                </Box>

                {tempAmostra !== undefined && tempAmostra !== null && (
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: 'primary.50',
                            borderColor: 'primary.200',
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            justifyContent="center"
                        >
                            <Typography
                                variant="body2"
                                fontWeight={600}
                                color="primary.main"
                            >
                                Temperatura da Amostra:
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight={800}
                                color="primary.main"
                            >
                                {fmtTemp(tempAmostra)}
                            </Typography>
                        </Stack>
                    </Paper>
                )}

                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                        <Paper
                            variant="outlined"
                            sx={{ p: 1.2, borderRadius: 2, height: '100%' }}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <Icon
                                    color={
                                        chipColor === 'default'
                                            ? theme.palette.action.active
                                            : theme.palette[chipColor]?.main
                                    }
                                />
                                <Stack>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Condição
                                    </Typography>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        flexWrap="wrap"
                                        useFlexGap
                                    >
                                        <Chip
                                            size="small"
                                            label={label}
                                            color={chipColor}
                                            variant="outlined"
                                        />
                                        {!!weather?.description && (
                                            <Typography
                                                variant="body2"
                                                fontWeight={700}
                                            >
                                                {String(weather.description)}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper
                            variant="outlined"
                            sx={{ p: 1.2, borderRadius: 2, height: '100%' }}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <Lucide.Clock color={theme.palette.info.main} />
                                <Stack>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Observado em
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight={800}
                                    >
                                        {fmtUtcToLocal(weather?.observedAtUtc)}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Paper
                            variant="outlined"
                            sx={{ p: 1, borderRadius: 2 }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                align="center"
                                display="block"
                            >
                                Temp. Ambiente
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={0.8}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <Lucide.Sun size={18} color={theme.palette.warning.main} />
                                <Typography variant="body1" fontWeight={900}>
                                    {fmtTemp(weather?.temperature)}
                                </Typography>
                            </Stack>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Paper
                            variant="outlined"
                            sx={{ p: 1, borderRadius: 2 }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                align="center"
                                display="block"
                            >
                                Umidade
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={0.8}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <Lucide.Droplets size={18} color={theme.palette.info.main} />
                                <Typography variant="body1" fontWeight={900}>
                                    {fmtPct(weather?.humidity)}
                                </Typography>
                            </Stack>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Paper
                            variant="outlined"
                            sx={{ p: 1, borderRadius: 2 }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                align="center"
                                display="block"
                            >
                                Pressão
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={0.8}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <Lucide.Gauge size={18} color={theme.palette.success.main} />
                                <Typography variant="body1" fontWeight={900}>
                                    {fmtHpa(weather?.pressure)}
                                </Typography>
                            </Stack>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Paper
                            variant="outlined"
                            sx={{ p: 1, borderRadius: 2 }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                align="center"
                                display="block"
                            >
                                Vento
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={0.8}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <Lucide.Wind size={18} color={theme.palette.primary.main} />
                                <Typography variant="body1" fontWeight={900}>
                                    {fmtWind(weather?.windSpeed)}
                                </Typography>
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>

                {!hasWeather && (
                    <Alert severity="info" variant="outlined">
                        Nenhum dado de clima disponível para esta análise.
                    </Alert>
                )}
            </Stack>
        </Paper>
    );
};

// COMPONENTE NOVO QUE EXIBE AS TORRES COM GRID FLUIDO (Resolve o Bug de Margin Negative do MUI)
const TorresVinculadasBox = ({ amostra, torresDetalhes }) => {
    const theme = useTheme();
    const torres = amostra?.torres || [];
    const amostraStatus = upper(amostra?.status);
    const [analiseNomes, setAnaliseNomes] = useState({});

    useEffect(() => {
        // O componente faz early-return (null) fora de PENDENTE/EM_PROCESSAMENTO, mas o
        // efeito roda ANTES disso: sem esta guarda, abrir os Detalhes de uma analise
        // encerrada baixava ate 2000 torres para nao renderizar nada.
        if (amostraStatus !== 'PENDENTE' && amostraStatus !== 'EM_PROCESSAMENTO') return;
        if (!torres.length) return;

        const protocolosAnalises = amostra?.protocolosAnalises || [];
        const analiseNomeById = {};
        protocolosAnalises.forEach((pa) => {
            if (pa.analiseId) analiseNomeById[pa.analiseId] = pa.analiseNome;
        });

        const map = {};
        const missingIds = [];

        torres.forEach((torre) => {
            const full = (torresDetalhes || []).find(
                (t) => t.torreId === torre.torreId
            );
            const analysisId = full?.experiments?.[0]?.analysisId;
            if (analysisId) {
                map[torre.torreId] = analiseNomeById[analysisId] || null;
            } else {
                missingIds.push(torre.torreId);
            }
        });

        if (missingIds.length === 0) {
            setAnaliseNomes(map);
            return;
        }

        getTowers(0, 2000)
            .then((data) => {
                (data?.content || []).forEach((tower) => {
                    if (missingIds.includes(tower.id)) {
                        const analysisId = tower?.protocol?.analysisId;
                        map[tower.id] = analysisId
                            ? analiseNomeById[analysisId] || null
                            : null;
                    }
                });
                setAnaliseNomes(map);
            })
            .catch(() => setAnaliseNomes(map));
    }, [amostra, torresDetalhes, amostraStatus]);

    if (amostraStatus !== 'PENDENTE' && amostraStatus !== 'EM_PROCESSAMENTO') {
        return null;
    }

    const getTowerStatusColor = (status) => {
        switch (upper(status)) {
            case 'VINCULADA':
                return 'success';
            case 'PENDENTE':
                return 'warning';
            case 'EM_PROCESSAMENTO':
            case 'PROCESSANDO':
                return 'info';
            case 'CONCLUIDO':
            case 'ENCERRADA':
                return 'success';
            case 'ERRO':
                return 'error';
            default:
                return 'default';
        }
    };

    const getTowerBgColor = (status) => {
        switch (upper(status)) {
            case 'VINCULADA':
                return alpha(theme.palette.success.main, 0.08);
            case 'PENDENTE':
                return alpha(theme.palette.warning.main, 0.08);
            case 'EM_PROCESSAMENTO':
            case 'PROCESSANDO':
                return alpha(theme.palette.info.main, 0.08);
            case 'CONCLUIDO':
            case 'ENCERRADA':
                return alpha(theme.palette.success.main, 0.08);
            case 'ERRO':
                return alpha(theme.palette.error.main, 0.08);
            default:
                return alpha(theme.palette.background.default, 0.8);
        }
    };

    const getAnaliseColorType = (nome) => {
        if (!nome) return null;
        const lower = nome.toLowerCase();
        if (lower.includes('afluente')) return 'error';
        if (lower.includes('efluente')) return 'info';
        return null;
    };

    const FormatDiluicao = ({ value }) => {
        if (value === null || value === undefined || value === '') {
            return (
                <Typography
                    variant="body2"
                    fontWeight={600}
                    color="text.disabled"
                >
                    Amostra Pura (Sem diluição)
                </Typography>
            );
        }

        const str = String(value).trim().toLowerCase();
        let exp = str;

        if (str.startsWith('10^')) {
            exp = str.replace('10^', '');
        } else if (str.startsWith('10') && str.length > 2) {
            exp = str.substring(2);
        } else if (!isNaN(Number(str))) {
            exp = str;
            if (!exp.startsWith('-') && Number(exp) > 0) {
                exp = `-${exp}`;
            }
        }

        return (
            <Typography variant="body2" fontWeight={600} color="text.primary">
                10
                <sup style={{ fontSize: '0.85em', marginLeft: '1px' }}>
                    {exp}
                </sup>
            </Typography>
        );
    };

    if (!torres.length) {
        return (
            <Alert severity="info" variant="outlined" sx={{ width: '100%' }}>
                Nenhum dispositivo vinculado a esta análise.
            </Alert>
        );
    }

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Lucide.FlaskConical color={theme.palette.action.active} />
                    <Typography fontWeight={800} variant="subtitle1">
                        Dispositivos Vinculados ({torres.length})
                    </Typography>
                </Stack>
                <Divider />

                {/* SOLUÇÃO CSS GRID NATIVO: Elimina o bug do MUI de margin-negative no Grid Container que puxava o cartão da direita para cima da borda */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                    }}
                >
                    {torres.map((t, idx) => {
                        const statusColor = getTowerStatusColor(t.status);
                        const analiseColor = getAnaliseColorType(
                            analiseNomes[t.torreId]
                        );
                        const effectiveColor = analiseColor ?? statusColor;
                        const bgColor = alpha(
                            theme.palette[effectiveColor]?.main ||
                                theme.palette.divider,
                            0.08
                        );
                        const borderColor = alpha(
                            theme.palette[effectiveColor]?.main ||
                                theme.palette.divider,
                            0.4
                        );

                        return (
                            <Paper
                                key={idx}
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: bgColor,
                                    borderColor: borderColor,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={1.5}
                                    gap={1}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight="bold"
                                        fontFamily="monospace"
                                        noWrap
                                    >
                                        {t.numeroSerie || 'Serial Desconhecido'}
                                    </Typography>
                                    <Chip
                                        label={t.status || 'N/A'}
                                        size="small"
                                        color={statusColor}
                                        sx={{
                                            fontWeight: 'bold',
                                            height: 22,
                                            fontSize: '0.65rem',
                                            flexShrink: 0,
                                        }}
                                    />
                                </Stack>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    Diluição Configurada
                                </Typography>
                                <FormatDiluicao value={t.diluicao} />
                                {analiseNomes[t.torreId] && (
                                    <>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            mt={1}
                                        >
                                            Análise
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            color="text.primary"
                                        >
                                            {analiseNomes[t.torreId]}
                                        </Typography>
                                    </>
                                )}
                            </Paper>
                        );
                    })}
                </Box>
            </Stack>
        </Paper>
    );
};

const ExperimentosBox = ({
    resultadoAmostra,
    tenant,
    protocolosAnalises = [],
}) => {
    const theme = useTheme();
    const exps = Array.isArray(resultadoAmostra?.resultadosExperimento)
        ? resultadoAmostra.resultadosExperimento
        : [];
    if (!exps.length) {
        return (
            <Alert severity="info" variant="outlined">
                Nenhuma execução de experimento encontrada.
            </Alert>
        );
    }
    const sorted = [...exps].sort((a, b) => {
        const ta = new Date(a?.startedAt || a?.createdAt || 0).getTime();
        const tb = new Date(b?.startedAt || b?.createdAt || 0).getTime();
        return tb - ta;
    });
    const stats = sorted.reduce(
        (acc, e) => {
            const st = upper(e?.status);
            acc.total += 1;
            if (st === 'CONCLUIDO') acc.concluidos += 1;
            if (st === 'FALHO' || e?.erro) acc.falhos += 1;
            if (e?.resultado) acc.comResultado += 1;
            return acc;
        },
        { total: 0, concluidos: 0, falhos: 0, comResultado: 0 }
    );

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Lucide.History color={theme.palette.action.active} />
                        <Typography fontWeight={800} variant="subtitle1">
                            Execuções (Amostras)
                        </Typography>
                    </Stack>
                    <Typography
                        variant="caption"
                        sx={{
                            bgcolor: 'action.selected',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                        }}
                    >
                        Total: {stats.total} | OK: {stats.concluidos} | Erro:{' '}
                        {stats.falhos}
                    </Typography>
                </Stack>
                <Box>
                    {sorted.map((exp, idx) => {
                        const experimentId =
                            exp?.experimentId || exp?.id || null;
                        const graficoUrl = buildGraficoPath(
                            tenant,
                            experimentId
                        );
                        const st = resultadoChip(exp?.status);
                        const stage = deriveStageFromExperiment(exp);

                        const started = exp?.startedAt
                            ? new Date(exp.startedAt).getTime()
                            : null;
                        const finished = exp?.finishedAt
                            ? new Date(exp.finishedAt).getTime()
                            : null;
                        const durationMs =
                            started && finished ? finished - started : null;

                        const r = exp?.resultado || null;

                        const matchAnalise = protocolosAnalises.find(
                            (pa) =>
                                normalizeId(pa.analiseId) ===
                                normalizeId(exp?.analysisId)
                        );
                        const labelAnalise =
                            matchAnalise?.analiseNome || exp?.analysisId || '—';

                        let serialDaTorre = null;
                        if (typeof exp?.serialNumber === 'string') {
                            serialDaTorre = exp.serialNumber;
                        } else if (typeof exp?.numeroSerie === 'string') {
                            serialDaTorre = exp.numeroSerie;
                        } else if (typeof exp?.torre === 'string') {
                            serialDaTorre = exp.torre;
                        } else if (
                            typeof exp?.torre === 'object' &&
                            exp.torre !== null
                        ) {
                            serialDaTorre =
                                exp.torre.serialNumber ||
                                exp.torre.numeroSerie ||
                                exp.torre.id ||
                                null;
                        }

                        if (
                            typeof serialDaTorre === 'object' ||
                            !serialDaTorre
                        ) {
                            serialDaTorre = '—';
                        }

                        return (
                            <Accordion
                                key={`${experimentId || 'exp'}-${idx}`}
                                disableGutters
                                variant="outlined"
                                sx={{ mb: 1, '&:before': { display: 'none' } }}
                            >
                                <AccordionSummary
                                    expandIcon={<Lucide.ChevronDown size={20} />}
                                >
                                    <Grid
                                        container
                                        spacing={1}
                                        alignItems="center"
                                    >
                                        <Grid item xs={12} sm={4}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={700}
                                                noWrap
                                                sx={{ fontFamily: 'monospace' }}
                                            >
                                                {serialDaTorre}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                noWrap
                                            >
                                                {labelAnalise}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={8}>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                flexWrap="wrap"
                                                gap={0.5}
                                            >
                                                <Chip
                                                    size="small"
                                                    label={st.label}
                                                    color={st.color}
                                                    sx={{ height: 24 }}
                                                />
                                                <Chip
                                                    size="small"
                                                    label={`Etapa: ${stage}`}
                                                    variant="outlined"
                                                    sx={{ height: 24 }}
                                                />
                                                {durationMs !== null && (
                                                    <Chip
                                                        size="small"
                                                        label={msToHuman(
                                                            durationMs
                                                        )}
                                                        icon={
                                                            <Lucide.Clock
                                                                size={14}
                                                            />
                                                        }
                                                        variant="outlined"
                                                        sx={{ height: 24 }}
                                                    />
                                                )}
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{
                                        bgcolor: 'action.hover',
                                        borderTop: '1px solid rgba(0,0,0,0.12)',
                                    }}
                                >
                                    <Stack spacing={2}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Stack
                                                    direction="row"
                                                    justifyContent="space-between"
                                                    alignItems="flex-start"
                                                    flexWrap="wrap"
                                                    gap={1}
                                                >
                                                    <Box>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            display="block"
                                                        >
                                                            Analysis & Modo
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontFamily:
                                                                    'monospace',
                                                                wordBreak:
                                                                    'break-all',
                                                            }}
                                                        >
                                                            {fmt(labelAnalise)}{' '}
                                                            (
                                                            {fmt(
                                                                exp?.analysisMode ||
                                                                    r?.analysis_mode
                                                            )}
                                                            )
                                                        </Typography>
                                                    </Box>
                                                    {graficoUrl && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                            href={graficoUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            startIcon={
                                                                <Lucide.ExternalLink size={18} />
                                                            }
                                                        >
                                                            Abrir grafico
                                                        </Button>
                                                    )}
                                                </Stack>
                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <FieldLine
                                                    label="Criado"
                                                    value={fmtDate(
                                                        exp?.createdAt
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <FieldLine
                                                    label="Iniciado"
                                                    value={fmtDate(
                                                        exp?.startedAt
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <FieldLine
                                                    label="Finalizado"
                                                    value={fmtDate(
                                                        exp?.finishedAt
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <FieldLine
                                                    label="BioAILab"
                                                    value={serialDaTorre}
                                                />
                                            </Grid>
                                        </Grid>
                                        {exp?.erro && (
                                            <Alert
                                                severity="error"
                                                icon={<Lucide.CircleAlert />}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    fontWeight={700}
                                                >
                                                    Erro
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {String(exp.erro)}
                                                </Typography>
                                            </Alert>
                                        )}
                                        {r ? (
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    bgcolor: 'background.paper',
                                                }}
                                            >
                                                <Stack spacing={2}>
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                    >
                                                        <Typography
                                                            fontWeight={700}
                                                            variant="body2"
                                                            color="primary"
                                                        >
                                                            RESULTADO /
                                                            PREDIÇÕES
                                                        </Typography>
                                                    </Stack>
                                                    {('presence_colitotais' in
                                                        r ||
                                                        'presence_ecoli' in
                                                            r) && (
                                                        <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            flexWrap="wrap"
                                                        >
                                                            {'presence_colitotais' in
                                                                r && (
                                                                <Chip
                                                                    label={`Coliformes: ${r.presence_colitotais ? 'PRESENTE' : 'AUSENTE'}`}
                                                                    color={
                                                                        r.presence_colitotais
                                                                            ? 'warning'
                                                                            : 'default'
                                                                    }
                                                                    variant={
                                                                        r.presence_colitotais
                                                                            ? 'filled'
                                                                            : 'outlined'
                                                                    }
                                                                />
                                                            )}
                                                            {'presence_ecoli' in
                                                                r && (
                                                                <Chip
                                                                    label={`E.coli: ${r.presence_ecoli ? 'PRESENTE' : 'AUSENTE'}`}
                                                                    color={
                                                                        r.presence_ecoli
                                                                            ? 'warning'
                                                                            : 'default'
                                                                    }
                                                                    variant={
                                                                        r.presence_ecoli
                                                                            ? 'filled'
                                                                            : 'outlined'
                                                                    }
                                                                />
                                                            )}
                                                        </Stack>
                                                    )}
                                                    <Divider />
                                                    {/* Somente NMP. O modelo nao prediz UFC:
                                                        buildPredictionResult (inference-go)
                                                        atribui a MESMA variavel aos dois
                                                        campos, entao "UFC" era uma copia do
                                                        NMP — e mostrar as duas sugeria uma
                                                        contagem em placa que nunca existiu. */}
                                                    <Grid container spacing={1}>
                                                        <Grid
                                                            item
                                                            xs={6}
                                                            sm={6}
                                                        >
                                                            <PredField
                                                                label="Coliformes (NMP)"
                                                                value={
                                                                    r.predict_colitotais_nmp
                                                                }
                                                                error={
                                                                    r.predict_colitotais_nmp_error
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={6}
                                                            sm={6}
                                                        >
                                                            <PredField
                                                                label="E.coli (NMP)"
                                                                value={
                                                                    r.predict_ecoli_nmp
                                                                }
                                                                error={
                                                                    r.predict_ecoli_nmp_error
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Stack>
                                            </Paper>
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    fontStyle: 'italic',
                                                    textAlign: 'center',
                                                    py: 1,
                                                }}
                                            >
                                                Sem objeto de resultado
                                                retornado.
                                            </Typography>
                                        )}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Box>
            </Stack>
        </Paper>
    );
};

const ResultadoBoxResumo = ({
    resultadoAmostra,
    invalidaVisivel = false,
    detalhesTecnicosAbertos = false,
}) => {
    if (!resultadoAmostra) {
        return (
            <Alert severity="info" variant="outlined" sx={{ width: '100%' }}>
                Nenhum resultado disponível ainda.
            </Alert>
        );
    }
    const chip = resultadoChip(resultadoAmostra.status);
    // Nao repetir o alerta vermelho enquanto o calculo corre — ver invalidaVisivel.
    const resultadoInvalido = invalidaVisivel;
    const motivoInvalidacao = resultadoAmostra?.motivoInvalidacao;
    const amostraObj = resultadoAmostra?.amostra || {};
    const exps = Array.isArray(resultadoAmostra?.resultadosExperimento)
        ? resultadoAmostra.resultadosExperimento
        : [];
    const lastFinished = exps
        .filter((e) => e?.finishedAt)
        .sort(
            (a, b) =>
                new Date(b.finishedAt).getTime() -
                new Date(a.finishedAt).getTime()
        )[0];

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                >
                    <Typography fontWeight={800} variant="subtitle1">
                        Resumo Global
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        <Chip
                            size="small"
                            label={resultadoInvalido ? 'Invalida' : chip.label}
                            color={resultadoInvalido ? 'error' : chip.color}
                        />
                        {lastFinished?.finishedAt && (
                            <Chip
                                size="small"
                                icon={<Lucide.Clock />}
                                label={
                                    'Última finalização: ' +
                                    fmtDate(lastFinished.finishedAt)
                                }
                                variant="outlined"
                                sx={{
                                    height: 'auto',
                                    maxWidth: '100%',
                                    '& .MuiChip-label': {
                                        whiteSpace: 'normal',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    },
                                    py: 0.5,
                                }}
                            />
                        )}
                    </Stack>
                </Stack>
                {resultadoInvalido && (
                    <Alert severity="error" variant="filled">
                        <Typography fontWeight={700}>
                            Resultado invalido
                        </Typography>
                        <Typography sx={{ wordBreak: 'break-word' }}>
                            {motivoInvalidacao ||
                                'A amostra foi marcada como invalida pelas regras de validacao.'}
                        </Typography>
                    </Alert>
                )}
                <Divider />

                {/* Identificadores tecnicos: fechado por padrao, aberto para ADMIN.
                    NAO some para os demais de proposito — quando o gestor liga
                    dizendo "essa analise esta errada", a primeira pergunta e QUAL,
                    e o ID e a resposta. Esconder de vez impediria o cliente de
                    ajudar o suporte. O padrao aqui e tirar do caminho, nao do
                    alcance.

                    Saiu daqui o que era ruido puro: `Tenant` aparecia DUAS vezes
                    (e e sempre o mesmo da sessao), `Identificacao` repetia o titulo
                    do proprio modal, e `Matriz` agora so aparece quando preenchida. */}
                <Accordion
                    defaultExpanded={detalhesTecnicosAbertos}
                    disableGutters
                    elevation={0}
                    sx={{
                        bgcolor: 'transparent',
                        '&:before': { display: 'none' },
                    }}
                >
                    <AccordionSummary
                        expandIcon={<Lucide.ChevronDown size={18} />}
                        sx={{ px: 0, minHeight: 0 }}
                    >
                        <Typography variant="subtitle2" color="text.secondary">
                            Dados técnicos
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0 }}>
                        <Stack spacing={1}>
                            <FieldLine
                                label="Resultado ID"
                                value={resultadoAmostra?.id}
                                mono
                            />
                            <FieldLine
                                label="Análise ID"
                                value={amostraObj?.amostraId}
                                mono
                            />
                            <FieldLine
                                label="Criado em"
                                value={fmtDate(resultadoAmostra?.createdAt)}
                            />
                            {amostraObj?.tipoMatriz && (
                                <FieldLine
                                    label="Matriz"
                                    value={amostraObj.tipoMatriz}
                                />
                            )}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            </Stack>
        </Paper>
    );
};

const AnaliseDetalhesModal = ({
    open,
    onClose,
    amostra,
    detalhes,
    loading,
    error,
    onRefresh,
    limite = LIMITE_PADRAO,
    buffer = BUFFER_PADRAO,
    mostrarEficiencia = false,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { tenant } = useParams();
    const { pode } = usePermissao();
    const { bootstrap } = useBootstrap();

    const REPORT_TAG = 'RESULTADO';
    const amostraId = amostra?.id || amostra?._id;
    const [resultadoAmostra, setResultadoAmostra] = useState(null);

    // O prop `amostra` e um RETRATO do instante em que o modal abriu. O polling
    // abaixo atualiza o resultado, mas ninguem atualiza a amostra — entao o
    // status dela envelhece com o modal aberto. Como estadoResultado() testa
    // EM_PROCESSAMENTO primeiro, o bloco de eficiencia ficava preso em
    // "Experimento em andamento" mesmo depois do calculo terminar, e so um F5
    // destravava.
    //
    // Um resultado pronto e a prova de que o experimento terminou: nesse caso o
    // status do retrato esta desatualizado e nao pode mandar na tela.
    const statusAmostraEfetivo =
        isResultadoPronto(resultadoAmostra) &&
        upper(amostra?.status) === 'EM_PROCESSAMENTO'
            ? 'FINALIZADA'
            : amostra?.status;

    // Chip de status do topo: usa o status EFETIVO, senao continuava anunciando
    // "Em processamento" depois do calculo ter terminado.
    const chipAmostra = statusChip(statusAmostraEfetivo);

    // Estado do bloco de eficiencia (mesma regra do card — utils/eficiencia.js).
    const estadoEficiencia = useMemo(
        () =>
            estadoResultado({
                status: statusAmostraEfetivo,
                temResultado: !!resultadoAmostra,
                resultadoStatus: resultadoAmostra?.status,
                eficienciaEcoli: resultadoAmostra?.eficienciaEcoli,
                eficienciaInvalida: resultadoAmostra?.invalida,
                eficienciaMotivo: resultadoAmostra?.motivoInvalidacao,
            }),
        [statusAmostraEfetivo, resultadoAmostra]
    );

    const [sending, setSending] = useState(false);
    const [reprocessing, setReprocessing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [calculando, setCalculando] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    // Comentarios: estado local semeado do servidor. Sem isso, um comentario novo so
    // apareceria no proximo refetch do modal — e ele tem polling proprio, entao a nota
    // sumiria por alguns segundos logo depois de escrita.
    const [comentariosOpen, setComentariosOpen] = useState(false);
    const [comentariosLocais, setComentariosLocais] = useState(null);

    // Enquanto o calculo corre, o backend JA gravou invalida=true — ele avalia o
    // documento recem-criado, que ainda nao tem leitura nenhuma de E.coli, e
    // conclui "dados insuficientes". Exibir "Invalida" nesse intervalo e um falso
    // erro: o resultado sequer chegou. A precedencia certa ja existe em
    // estadoResultado() (calculando vem ANTES de invalida) — basta respeita-la.
    // Duas fases distintas, antes coladas na mesma variavel:
    //  - analiseEmAndamento: amostras rodando no dispositivo (horas). Nada vai
    //    para o modelo aqui — mostrar barra de progresso de modelo era mentira.
    //  - calculandoModelo: o modelo rodando (segundos), pos Calcular/Reprocessar.
    const analiseEmAndamento = estadoEficiencia.key === 'em-andamento';
    const calculandoModelo =
        estadoEficiencia.key === 'calculando' || calculando;

    // Para o que vale nas DUAS fases: nao acusar invalido (ainda nao ha leitura
    // de E.coli em nenhuma das duas) e travar as acoes que mexem no resultado.
    const emCurso = analiseEmAndamento || calculandoModelo;

    // FONTE UNICA de "esta invalido de verdade". Todo lugar que pintava de
    // vermelho lia `invalida` cru e acusava erro no meio do processamento: os dois
    // chips do topo, o bloco de eficiencia, o alerta do resumo e a timeline.
    const invalidaVisivel = !emCurso && resultadoAmostra?.invalida === true;

    // Progresso real do calculo, para a barra: um item esta pronto quando tem
    // resultado, falhou ou registrou erro (mesmo criterio de isResultadoPronto).
    const progressoCalculo = useMemo(() => {
        const itens = Array.isArray(resultadoAmostra?.resultadosExperimento)
            ? resultadoAmostra.resultadosExperimento
            : [];
        const total = itens.length;
        const prontos = itens.filter(
            (e) => !!e?.resultado || upper(e?.status) === 'FALHO' || !!e?.erro
        ).length;
        return {
            total,
            prontos,
            pct: total > 0 ? Math.round((prontos / total) * 100) : 0,
        };
    }, [resultadoAmostra]);
    const [snack, setSnack] = useState({
        open: false,
        severity: 'success',
        message: '',
    });

    const openSnack = (severity, message) =>
        setSnack({ open: true, severity, message });

    useEffect(() => {
        if (!open || !amostraId) return;
        let cancelled = false;
        setCalculando(false);
        buscarUltimoResultadoAmostra(amostraId)
            .then((r) => {
                if (cancelled) return;
                setResultadoAmostra(r || null);
                // Ha resultado, ainda nao terminou E foi criado ha pouco => polling ao vivo.
                if (r && !isResultadoPronto(r) && resultadoRecente(r)) {
                    setCalculando(true);
                }
            })
            .catch(() => {
                if (!cancelled) setResultadoAmostra(null);
            });
        return () => {
            cancelled = true;
        };
    }, [open, amostraId]);

    // Polling ao vivo enquanto "calculando" (ao reabrir pos-Calcular, ou pos-Reprocessar):
    // atualiza o resultado no modal a cada 2s ate o modelo terminar. Cancela ao fechar.
    useEffect(() => {
        if (!open || !amostraId || !calculando) return;
        let cancelled = false;
        (async () => {
            // ~2 min de janela (60 x 2s). Antes eram 30s, que bastavam porque o
            // modal FECHAVA no Calcular; agora ele fica aberto acompanhando, e um
            // teto curto deixaria a barra congelada se a fila atrasasse. Um ensaio
            // real de 4 dispositivos leva ~17s, entao ha folga larga.
            for (let i = 0; i < 60 && !cancelled; i++) {
                await sleep(2000);
                if (cancelled) return;
                try {
                    const r = await buscarUltimoResultadoAmostra(amostraId);
                    if (cancelled) return;
                    if (r) setResultadoAmostra(r);
                    if (isResultadoPronto(r)) {
                        setCalculando(false);
                        return;
                    }
                } catch {
                    /* tenta de novo */
                }
            }
            if (!cancelled) setCalculando(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [open, amostraId, calculando]);

    // Com um unico protocolo no tenant, o par Protocolo/Analise e sempre o mesmo
    // texto — nao distingue analise nenhuma. So volta a informar quando ha mais de
    // um protocolo cadastrado.
    const protocoloVaria = useMemo(() => {
        const protocolos = bootstrap?.protocolos || bootstrap?.protocols || [];
        return protocolos.length > 1;
    }, [bootstrap]);

    const { protocolName, analysisName } = useMemo(() => {
        const protocolosAmostra =
            amostra?.protocolosAnalises || detalhes?.protocolosAnalises || [];
        const torres = amostra?.torres || detalhes?.torres || [];

        const executedAnalysisIds = new Set();

        torres.forEach((t) => {
            if (Array.isArray(t.experiments)) {
                t.experiments.forEach((e) => {
                    if (e.analysisId)
                        executedAnalysisIds.add(normalizeId(e.analysisId));
                });
            }
        });

        if (
            resultadoAmostra &&
            Array.isArray(resultadoAmostra.resultadosExperimento)
        ) {
            resultadoAmostra.resultadosExperimento.forEach((e) => {
                if (e.analysisId)
                    executedAnalysisIds.add(normalizeId(e.analysisId));
            });
        }

        const idsToLookup =
            executedAnalysisIds.size > 0
                ? Array.from(executedAnalysisIds)
                : protocolosAmostra
                      .map((pa) => normalizeId(pa.analiseId))
                      .filter(Boolean);

        const foundProtocols = new Set();
        const foundAnalyses = new Set();

        idsToLookup.forEach((aId) => {
            const match = protocolosAmostra.find(
                (pa) => normalizeId(pa.analiseId) === aId
            );
            if (match) {
                if (match.protocoloNome)
                    foundProtocols.add(match.protocoloNome.trim());
                if (match.analiseNome)
                    foundAnalyses.add(match.analiseNome.trim());
            } else if (bootstrap?.protocolos) {
                bootstrap.protocolos.forEach((p) => {
                    const anal = (p.analysis || p.analises || []).find(
                        (a) => normalizeId(a?.id || a?._id) === aId
                    );
                    if (anal) {
                        foundProtocols.add(p.name || p.nome || '');
                        foundAnalyses.add(anal.name || anal.nome || '');
                    }
                });
            }
        });

        const finalProtocols = Array.from(foundProtocols).filter(Boolean);
        const finalAnalyses = Array.from(foundAnalyses).filter(Boolean);

        return {
            protocolName:
                finalProtocols.length > 0 ? finalProtocols.join(' + ') : '—',
            analysisName:
                finalAnalyses.length > 0 ? finalAnalyses.join(' + ') : '—',
        };
    }, [amostra, detalhes, resultadoAmostra, bootstrap]);

    const amostraStatus = upper(amostra?.status);
    const amostraInvalida = invalidaVisivel || amostraStatus === 'INVALIDA';
    // Espelha validarAmostraProntaParaFila no backend: so calcula com os
    // experimentos JA encerrados. EM_PROCESSAMENTO estava nesta lista, entao o
    // botao aparecia habilitado durante o ensaio e o clique voltava erro.
    const canSend = ['FINALIZADA', 'ENCERRADA', 'ENVIADO'].includes(
        amostraStatus
    );
    const canReprocess = !!resultadoAmostra?.id;
    const canDeleteResultados = !!resultadoAmostra?.id;

    const exps = useMemo(() => {
        return Array.isArray(resultadoAmostra?.resultadosExperimento)
            ? resultadoAmostra.resultadosExperimento
            : [];
    }, [resultadoAmostra]);
    const resultadoFinalizado = useMemo(() => {
        if (!resultadoAmostra?.id) return false;
        if (exps.length === 0) return false;

        const anyError = exps.some(
            (e) => upper(e?.status) === 'FALHO' || !!e?.erro
        );
        if (anyError) return false;

        // TODOS, nao algum. Antes eram dois `some`, entao bastava UM dispositivo
        // ter voltado para o "Relatorio Pronto" acender e o PDF liberar — e o
        // laudo saia com as medias geometricas calculadas sobre uma fracao da
        // amostragem. Num documento de conformidade isso e inaceitavel.
        return exps.every((e) => !!e?.finishedAt && !!e?.resultado);
    }, [resultadoAmostra, exps]);
    const canDownloadPdf = resultadoFinalizado;
    const globalStage = useMemo(
        () => deriveGlobalStage(resultadoAmostra, emCurso),
        [resultadoAmostra, emCurso]
    );

    // Calcular: enfileira (assincrono) e MANTEM o modal aberto, acompanhando o
    // progresso ao vivo — igual ao Reprocessar. Fechar fazia sentido quando nao
    // havia o que mostrar; com a barra de progresso, fechar a tela na cara de quem
    // acabou de pedir o calculo e esconder exatamente o que a pessoa quer ver.
    // (Clicar em Calcular pelo CARD, com o modal fechado, segue igual: o card e
    // quem trata aquele fluxo.)
    const handleSend = async () => {
        if (!pode('dados.alterar')) {
            openSnack('info', 'Modo Visitante: Envio bloqueado.');
            return;
        }
        setSending(true);
        try {
            await iniciarProcessamentoAmostra(amostraId);
            openSnack('info', 'Calculando o resultado...');
            // Dispara o polling que atualiza o modal ao vivo.
            setCalculando(true);
            // Atualiza a listagem por tras, para o card refletir o novo estado.
            onRefresh?.();
        } catch (e) {
            openSnack('error', e?.message || 'Falha ao enviar.');
        } finally {
            setSending(false);
        }
    };
    const handleReprocess = async () => {
        if (!pode('dados.alterar')) {
            openSnack('info', 'Modo Visitante: Reprocessamento bloqueado.');
            return;
        }
        if (!resultadoAmostra?.id) return;
        setReprocessing(true);
        try {
            await reprocessarResultadoAmostra(resultadoAmostra.id);
            openSnack(
                'info',
                'Reprocessando... o resultado atualiza aqui em instantes.'
            );
            // Mantem o modal aberto e dispara o polling que atualiza ao vivo.
            setCalculando(true);
        } catch (e) {
            openSnack('error', e?.message || 'Falha ao reprocessar.');
        } finally {
            setReprocessing(false);
        }
    };
    const handleDeleteResultados = () => {
        if (!pode('dados.alterar')) {
            openSnack(
                'info',
                'Modo Visitante: Exclusão de resultados bloqueada.'
            );
            return;
        }
        if (!amostraId) return;
        setConfirmDeleteOpen(true);
    };

    const confirmDeleteResultados = async () => {
        if (!amostraId) return;
        setDeleting(true);
        try {
            await excluirResultadosDaAmostra(amostraId);
            setResultadoAmostra(null);
            setConfirmDeleteOpen(false);
            openSnack(
                'success',
                'Resultados excluídos. Status da análise revertido.'
            );
            onRefresh?.();
        } catch (e) {
            openSnack('error', e?.message || 'Falha ao excluir resultados.');
        } finally {
            setDeleting(false);
        }
    };
    const handleDownloadPdf = async () => {
        if (!amostraId) return;
        setDownloadingPdf(true);
        try {
            const { blob, filename } = await baixarPdfTemplatePorAmostra({
                tenant,
                tag: REPORT_TAG,
                amostraId,
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || `relatorio-${amostraId}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            openSnack('success', 'PDF gerado e baixado com sucesso.');
        } catch (e) {
            openSnack('error', e?.message || 'Falha ao baixar PDF.');
        } finally {
            setDownloadingPdf(false);
        }
    };
    const busy =
        loading || sending || reprocessing || deleting || downloadingPdf;

    // `sending`/`reprocessing` so cobrem a REQUISICAO HTTP, que volta em
    // milissegundos: o CRM apenas enfileira e devolve. O calculo em si segue por
    // dezenas de segundos depois disso — e nesse intervalo os botoes voltavam a
    // ficar clicaveis. Um clique a mais publicava OUTRA mensagem para o MESMO
    // resultado, colocando dois workers a escrever no mesmo documento e rodando
    // cada dispositivo duas vezes no modelo.
    //
    // Tambem cobre Excluir Res.: apagar o resultado no meio do calculo deixaria
    // o worker gravando num documento que a tela ja considera inexistente.
    const acaoResultadoBloqueada = busy || emCurso;

    const amostraData = detalhes?.amostra || detalhes || amostra || {};
    const amostraIdAtual = amostraData?.id || amostraData?._id || null;

    // O estado local carrega o ID junto, entao ele se invalida sozinho quando o modal
    // passa a exibir OUTRA analise — sem precisar de efeito de limpeza.
    const comentarios =
        comentariosLocais && comentariosLocais.id === amostraIdAtual
            ? comentariosLocais.lista
            : amostraData?.comentarios;

    const adicionarComentario = async (texto) => {
        if (!amostraIdAtual) return;
        const salva = await adicionarComentarioAmostra(amostraIdAtual, texto);
        setComentariosLocais({
            id: amostraIdAtual,
            lista: salva?.comentarios || [],
        });
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={busy ? undefined : onClose}
                fullWidth
                maxWidth="md"
                fullScreen={isMobile}
                PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
            >
                <DialogTitle
                    sx={{
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 2,
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Lucide.BarChart3 />
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{ fontWeight: 600 }}
                        >
                            Detalhes da Análise
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{ color: 'inherit' }}
                        disabled={busy}
                    >
                        <Lucide.X />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{ bgcolor: 'background.default', p: isMobile ? 2 : 3 }}
                >
                    {loading ? (
                        <Stack alignItems="center" sx={{ py: 6 }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }} color="text.secondary">
                                Carregando dados da análise...
                            </Typography>
                        </Stack>
                    ) : error ? (
                        <Alert severity="error">{error}</Alert>
                    ) : !amostra ? (
                        <Typography align="center" color="text.secondary">
                            Nenhum dado disponível.
                        </Typography>
                    ) : (
                        <Stack spacing={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                }}
                            >
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                >
                                    Identificação
                                </Typography>
                                <Typography
                                    variant="h5"
                                    fontWeight={700}
                                    sx={{
                                        wordBreak: 'break-all',
                                        lineHeight: 1.2,
                                        mb: 1,
                                    }}
                                >
                                    {amostra.identificacao}
                                </Typography>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    flexWrap="wrap"
                                    gap={1}
                                >
                                    {amostraInvalida ? (
                                        <Chip
                                            size="small"
                                            label="Inválida"
                                            color="error"
                                        />
                                    ) : (
                                        <Chip
                                            size="small"
                                            label={chipAmostra.label}
                                            color={chipAmostra.color}
                                        />
                                    )}
                                    {resultadoAmostra?.status && (
                                        <Chip
                                            size="small"
                                            label={`Resultado: ${
                                                invalidaVisivel
                                                    ? 'Inválida'
                                                    : resultadoChip(
                                                          resultadoAmostra.status
                                                      ).label
                                            }`}
                                            color={
                                                invalidaVisivel
                                                    ? 'error'
                                                    : resultadoChip(
                                                          resultadoAmostra.status
                                                      ).color
                                            }
                                            variant="outlined"
                                        />
                                    )}
                                    {canDownloadPdf && (
                                        <Chip
                                            size="small"
                                            icon={<Lucide.CircleCheck />}
                                            label="Relatório Pronto"
                                            color="success"
                                            variant="outlined"
                                        />
                                    )}
                                </Stack>
                            </Paper>

                            {/* A timeline e do CALCULO. Sem resultado ainda (durante o
                                ensaio) ela seria so uma fileira de circulos vazios. */}
                            {resultadoAmostra && (
                                <TimelineEtapas stage={globalStage} />
                            )}

                            {/* FASE 1 — as amostras rodando no dispositivo. Dura horas
                                e nao envolve o modelo: nada de barra de progresso, que
                                aqui nao teria o que medir. */}
                            {analiseEmAndamento && (
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderColor: 'info.main',
                                        bgcolor: (t) =>
                                            alpha(t.palette.info.main, 0.06),
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1.5}
                                        alignItems="flex-start"
                                    >
                                        <Lucide.FlaskConical
                                            size={22}
                                            color={theme.palette.info.main}
                                        />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                fontWeight={800}
                                                color="info.main"
                                            >
                                                Análise em andamento
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block' }}
                                            >
                                                As amostras estão sendo analisadas
                                                nos dispositivos. O cálculo da
                                                eficiência fica disponível quando
                                                todas forem encerradas.
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            )}

                            {/* FASE 2 — o modelo rodando. Aqui sim ha o que medir:
                                quantos dispositivos ja voltaram. Barra determinada
                                quando sabemos o total; indeterminada antes disso,
                                que e quando nem a fila respondeu. */}
                            {calculandoModelo && (
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderColor: 'primary.main',
                                        bgcolor: (t) =>
                                            alpha(t.palette.primary.main, 0.06),
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1.5}
                                        alignItems="center"
                                        sx={{ mb: 1.25 }}
                                    >
                                        <CircularProgress size={22} />
                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Typography
                                                fontWeight={800}
                                                color="primary.main"
                                            >
                                                {estadoEficiencia.label ||
                                                    'Calculando resultado…'}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block' }}
                                            >
                                                {progressoCalculo.total > 0
                                                    ? `${progressoCalculo.prontos} de ${progressoCalculo.total} dispositivos processados`
                                                    : 'Enviando os dispositivos para o modelo…'}
                                                {' · a tela atualiza sozinha'}
                                            </Typography>
                                        </Box>
                                        {progressoCalculo.total > 0 && (
                                            <Typography
                                                variant="h6"
                                                fontWeight={800}
                                                color="primary.main"
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                {progressoCalculo.pct}%
                                            </Typography>
                                        )}
                                    </Stack>
                                    <LinearProgress
                                        variant={
                                            progressoCalculo.total > 0
                                                ? 'determinate'
                                                : 'indeterminate'
                                        }
                                        value={progressoCalculo.pct}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                </Paper>
                            )}

                            {mostrarEficiencia && !emCurso && (
                                <EficienciaResumo
                                    variant="detalhe"
                                    eficiencia={resultadoAmostra?.eficienciaEcoli}
                                    gmAfluente={resultadoAmostra?.gmEcoliAfluente}
                                    gmEfluente={resultadoAmostra?.gmEcoliEfluente}
                                    naoDetectado={
                                        resultadoAmostra?.efluenteNaoDetectado === true
                                    }
                                    invalida={invalidaVisivel}
                                    motivo={
                                        invalidaVisivel
                                            ? resultadoAmostra?.motivoInvalidacao
                                            : null
                                    }
                                    estado={estadoEficiencia}
                                    limite={limite}
                                    buffer={buffer}
                                />
                            )}

                            {/* Com UM protocolo so (caso do SANEAMENTO) isto e
                                texto constante: "Tratamento de Efluente" /
                                "Afluente + Efluente" em toda analise. Mesmo motivo
                                que tirou essas duas linhas do card da listagem —
                                rotulo fixo nao e informacao. Com multiplos
                                protocolos passa a distinguir, e volta a aparecer. */}
                            {protocoloVaria && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FieldLine
                                            label="Protocolo"
                                            value={protocolName}
                                            align="center"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FieldLine
                                            label="Análise"
                                            value={analysisName}
                                            align="center"
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                            )}

                            {/* Comentários — logo abaixo da eficiência de propósito: quem
                                abre os Detalhes por causa de um número divergente procura
                                exatamente aqui o que aconteceu naquele dia. */}
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack
                                    direction="row"
                                    alignItems="flex-start"
                                    justifyContent="space-between"
                                    gap={1}
                                    sx={{ mb: comentarios?.length ? 1.5 : 0 }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="overline"
                                            color="text.secondary"
                                        >
                                            Comentários
                                        </Typography>
                                        {!comentarios?.length && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Nenhum registro para esta análise.
                                            </Typography>
                                        )}
                                    </Box>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={
                                            <Lucide.MessageSquare size={16} />
                                        }
                                        onClick={() => setComentariosOpen(true)}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        {comentarios?.length
                                            ? `Ver (${comentarios.length})`
                                            : 'Comentar'}
                                    </Button>
                                </Stack>

                                {/* Prévia: só o mais recente. O histórico completo fica
                                    no diálogo, para não alongar um modal já extenso. */}
                                {!!comentarios?.length && (
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontStyle: 'italic',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            "{comentarios[comentarios.length - 1]?.text}"
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {comentarios[comentarios.length - 1]
                                                ?.author || 'Usuário'}
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>

                            <TorresVinculadasBox
                                amostra={amostraData}
                                torresDetalhes={detalhes?.torres}
                            />

                            {/* Execucoes por dispositivo ANTES do bloco tecnico: e o
                                que o laboratorista mais procura depois da eficiencia,
                                e estava enterrado sob os identificadores. */}
                            <ExperimentosBox
                                resultadoAmostra={resultadoAmostra}
                                tenant={tenant}
                                protocolosAnalises={
                                    amostra?.protocolosAnalises ||
                                    detalhes?.protocolosAnalises
                                }
                            />

                            <LocalizacaoClimaCard amostra={amostra} />

                            <ResultadoBoxResumo
                                resultadoAmostra={resultadoAmostra}
                                invalidaVisivel={invalidaVisivel}
                                detalhesTecnicosAbertos={pode('analise.detalhesTecnicosAbertos')}
                            />

                            {resultadoAmostra?.mensagemErro && (
                                <Alert severity="error" variant="filled">
                                    <Typography fontWeight={700}>
                                        Erro no processamento
                                    </Typography>
                                    <Typography
                                        sx={{ wordBreak: 'break-word' }}
                                    >
                                        {String(resultadoAmostra.mensagemErro)}
                                    </Typography>
                                </Alert>
                            )}
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        p: 2,
                        bgcolor: 'background.paper',
                        flexDirection: isMobile ? 'column-reverse' : 'row',
                        gap: isMobile ? 2 : 1,
                    }}
                >
                    <Button
                        onClick={onClose}
                        variant="text"
                        color="inherit"
                        fullWidth={isMobile}
                    >
                        Fechar
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    {canDeleteResultados && (
                        <Button
                            onClick={handleDeleteResultados}
                            disabled={acaoResultadoBloqueada}
                            variant="outlined"
                            color="error"
                            startIcon={<Lucide.Trash2 />}
                            fullWidth={isMobile}
                        >
                            {deleting ? 'Excluindo...' : 'Excluir Res.'}
                        </Button>
                    )}
                    {canReprocess && (
                        <Button
                            onClick={handleReprocess}
                            disabled={acaoResultadoBloqueada}
                            variant="outlined"
                            startIcon={<Lucide.RefreshCw />}
                            fullWidth={isMobile}
                        >
                            {reprocessing || calculandoModelo
                                ? 'Calculando...'
                                : 'Reprocessar'}
                        </Button>
                    )}
                    {canSend && (
                        <Button
                            onClick={handleSend}
                            disabled={acaoResultadoBloqueada}
                            variant="contained"
                            color="primary"
                            startIcon={<Lucide.Send />}
                            fullWidth={isMobile}
                        >
                            {sending || calculandoModelo
                                ? 'Calculando...'
                                : 'Calcular'}
                        </Button>
                    )}
                    <Tooltip
                        title={
                            canDownloadPdf
                                ? 'Baixar relatório final em PDF'
                                : 'PDF indisponível: aguarde finalizar e existir resultado válido.'
                        }
                        arrow
                        placement="top"
                    >
                        <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
                            <Button
                                onClick={handleDownloadPdf}
                                disabled={busy || !canDownloadPdf}
                                variant="contained"
                                color="success"
                                startIcon={
                                    downloadingPdf ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : (
                                        <Lucide.FileType />
                                    )
                                }
                                fullWidth={isMobile}
                                sx={{ minWidth: 160 }}
                            >
                                {downloadingPdf ? 'Baixando...' : 'Baixar PDF'}
                            </Button>
                        </Box>
                    </Tooltip>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
            >
                <Alert severity={snack.severity} variant="filled" elevation={6}>
                    {snack.message}
                </Alert>
            </Snackbar>

            <ComentariosAnaliseDialog
                open={comentariosOpen}
                onClose={() => setComentariosOpen(false)}
                identificacao={amostraData?.identificacao}
                comentarios={comentarios}
                onAdicionar={adicionarComentario}
            />

            <Dialog
                open={confirmDeleteOpen}
                onClose={deleting ? undefined : () => setConfirmDeleteOpen(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontWeight: 700,
                    }}
                >
                    <Lucide.TriangleAlert
                        size={20}
                        color={theme.palette.error.main}
                    />
                    Excluir resultado
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary">
                        Tem certeza que deseja excluir o resultado desta análise?
                        Isso apaga o <strong>ResultadoAmostra</strong> e todos os
                        itens internos, e a análise volta ao status anterior.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2, py: 1.5 }}>
                    <Button
                        onClick={() => setConfirmDeleteOpen(false)}
                        disabled={deleting}
                        color="inherit"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={confirmDeleteResultados}
                        disabled={deleting}
                        variant="contained"
                        color="error"
                        disableElevation
                        startIcon={
                            deleting ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <Lucide.Trash2 size={16} />
                            )
                        }
                    >
                        {deleting ? 'Excluindo...' : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AnaliseDetalhesModal;
